import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaEye,
  FaBell,
  FaComments,
  FaUser,
  FaBars,
  FaTimes,
  FaHome,
  FaEnvelope,
  FaCalendarAlt,
  FaCog,
  FaSignOutAlt,
} from 'react-icons/fa';
import './HostHeader.css';
import { logoutAtozasSession } from '../utils/atozasSession';

/**
 * Host header for standalone host pages (e.g. HostMessagesPage).
 * Uses navigate for all actions so the page feels inside the host area.
 */
const HostHeader = ({
  currentUser,
  unreadNotifCount = 0,
  unreadMessageCount = 0,
  pendingEnquiriesCount = 0,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const pathname = location.pathname || '';
  const isActive = (tab) => {
    if (pathname === '/host/messages') return tab === 'messages';
    if (pathname === '/host' && location.state?.openTab) return location.state.openTab === tab;
    if (pathname === '/host') return tab === 'dashboard';
    return false;
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setShowMobileMenu(false);
  };

  const handleLogoutConfirm = async () => {
    await logoutAtozasSession();
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        const userId = userObj._id || userObj.id;
        localStorage.removeItem(`officeSpaceAnswered_${userId}`);
        localStorage.removeItem(`meetingHomePopupShown_${userId}`);
      }
    } catch (error) {
      console.error('Error clearing user-specific data:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('hostProfileComplete');
    localStorage.removeItem('meetingHomePopupShown');
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('officeSpaceAnswered_') || key.startsWith('user_') || key.startsWith('booking_') || key.startsWith('lastSearch_') || key.startsWith('recentSearches_') || key.startsWith('meetingHomePopupShown_')) {
        localStorage.removeItem(key);
      }
    });
    window.location.href = '/';
  };

  const handleLogoutCancel = () => setShowLogoutModal(false);

  const goTo = (path, state) => {
    setShowMobileMenu(false);
    navigate(path, state ? { state } : {});
  };

  const name = currentUser?.name?.split(' ')[0] || currentUser?.email?.split('@')[0] || 'Host';

  return (
    <>
      <header className="host-header">
        <div className="host-header-left">
          <h1>Hi, {name} 👋</h1>
          <p>Manage your properties</p>
        </div>

        <div className="host-header-right">
          <button
            type="button"
            className="header-btn guest-view-btn"
            onClick={() => goTo('/')}
            title="Switch to Guest View"
          >
            <FaEye />
            <span className="btn-text">Guest View</span>
          </button>

          <button
            type="button"
            className="header-btn notification-btn"
            onClick={() => goTo('/host', { openTab: 'notifications' })}
            title="Notifications"
          >
            <FaBell />
            {unreadNotifCount > 0 && (
              <span className="header-badge">{unreadNotifCount}</span>
            )}
          </button>

          <button
            type="button"
            className="header-btn messages-btn"
            onClick={() => goTo('/host/messages')}
            title="Messages"
          >
            <FaComments />
            {unreadMessageCount > 0 && (
              <span className="header-badge">{unreadMessageCount}</span>
            )}
          </button>

          <button
            type="button"
            className="header-btn profile-btn"
            onClick={() => goTo('/host', { openTab: 'profile' })}
            title="Profile"
          >
            <FaUser />
          </button>

          <button
            type="button"
            className="header-btn menu-btn"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            title="Menu"
          >
            {showMobileMenu ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {showMobileMenu && (
          <div className="header-menu-dropdown">
            <button
              className={`menu-dropdown-item ${isActive('dashboard') ? 'active' : ''}`}
              onClick={() => goTo('/host')}
            >
              <FaHome />
              <span>Dashboard</span>
            </button>
            <button
              className={`menu-dropdown-item ${isActive('enquiries') ? 'active' : ''}`}
              onClick={() => goTo('/host', { openTab: 'enquiries' })}
            >
              <FaEnvelope />
              <span>Enquiries</span>
              {pendingEnquiriesCount > 0 && (
                <span className="dropdown-badge">{pendingEnquiriesCount}</span>
              )}
            </button>
            <button
              className={`menu-dropdown-item ${isActive('notifications') ? 'active' : ''}`}
              onClick={() => goTo('/host', { openTab: 'notifications' })}
            >
              <FaBell />
              <span>Notifications</span>
              {unreadNotifCount > 0 && (
                <span className="dropdown-badge">{unreadNotifCount}</span>
              )}
            </button>
            <button
              className={`menu-dropdown-item ${pathname === '/host/messages' ? 'active' : ''}`}
              onClick={() => goTo('/host/messages')}
            >
              <FaComments />
              <span>Messages</span>
              {unreadMessageCount > 0 && (
                <span className="dropdown-badge">{unreadMessageCount}</span>
              )}
            </button>
            <button
              className={`menu-dropdown-item ${isActive('properties') ? 'active' : ''}`}
              onClick={() => goTo('/host', { openTab: 'properties' })}
            >
              <FaHome />
              <span>Properties</span>
            </button>
            <button
              className={`menu-dropdown-item ${isActive('calendar') ? 'active' : ''}`}
              onClick={() => goTo('/host', { openTab: 'calendar' })}
            >
              <FaCalendarAlt />
              <span>Calendar</span>
            </button>
            <button
              className={`menu-dropdown-item ${isActive('profile') ? 'active' : ''}`}
              onClick={() => goTo('/host', { openTab: 'profile' })}
            >
              <FaUser />
              <span>Profile</span>
            </button>
            <button
              className={`menu-dropdown-item ${isActive('settings') ? 'active' : ''}`}
              onClick={() => goTo('/host', { openTab: 'settings' })}
            >
              <FaCog />
              <span>Settings</span>
            </button>
            <div className="menu-divider" />
            <button
              type="button"
              className="menu-dropdown-item logout-item"
              onClick={handleLogoutClick}
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </div>
        )}
      </header>

      {showLogoutModal && (
        <div className="host-header-logout-overlay" onClick={handleLogoutCancel}>
          <div className="host-header-logout-container" onClick={e => e.stopPropagation()}>
            <div className="host-header-logout-icon">
              <FaSignOutAlt />
            </div>
            <h2>Confirm Logout</h2>
            <p>Are you sure you want to logout? You'll need to sign in again to access your host dashboard.</p>
            <div className="host-header-logout-actions">
              <button type="button" className="btn-logout-cancel" onClick={handleLogoutCancel}>
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button type="button" className="btn-logout-confirm" onClick={handleLogoutConfirm}>
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HostHeader;
